import { supabase } from '@/integrations/supabase/client';
import { Match, Player, Team, Tournament, Wicket, ScoreEntry, Innings, FieldingStats, MatchEvent, PlayerCareerStats, PlayerRating, MatchState, InningsData, CreatePlayerPayload, UpdatePlayerPayload, CreateTournamentPayload, UpdateTournamentPayload, CreateTeamPayload, UpdateTeamPayload, MatchRow, ScoreHistoryRow, Feedback, Report, CreateFeedbackPayload, CreateReportPayload } from '@/types/cricket';
import { getRandomColor } from '@/store/useStore';
import { Database, Tables } from '@/integrations/supabase/types';

// Add this type at the top of the file (after imports):
type PlayerTeamJoin = { player_id: string; team_id: string };

type MatchWithJoins = Tables<'matches'> & {
  tournaments: {
    id: string;
    name: string;
    profile_tournaments: Array<{
      profile_id: string;
      role: string;
    }>;
  };
  'teams!matches_team1_id_fkey': {
    id: string;
    name: string;
    profile_teams: Array<{
      profile_id: string;
      role: string;
    }>;
  };
  'teams!matches_team2_id_fkey': {
    id: string;
    name: string;
    profile_teams: Array<{
      profile_id: string;
      role: string;
    }>;
  };
};

// Function to convert a number to base36
const toBase36 = (num: number): string => {
  return num.toString(36).toUpperCase();
};

// Function to generate a random string of specified length
const generateRandomString = (length: number): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Function to generate the custom unique_id
const generateUniqueId = (date: Date): string => {
  const yearOffset = date.getFullYear() - 2020;
  const month = date.getMonth() + 1;
  const day = date.getDate();

  if (yearOffset < 0 || month < 1 || month > 12 || day < 1 || day > 31) {
    console.warn("Date out of expected range for unique ID generation. Returning empty string.");
    return "";
  }

  const yearChar = toBase36(yearOffset);
  const monthChar = toBase36(month);
  const dayChar = toBase36(day);
  const randomComponent = generateRandomString(2);

  return `${yearChar}${monthChar}${dayChar}${randomComponent}`;
};

const uploadImage = async (bucketName: string, filePath: string, file: File) => {
  const { data, error } = await supabase.storage.from(bucketName).upload(filePath, file, {
    cacheControl: '3600',
    upsert: true,
  });

  if (error) {
    console.error(`Error uploading image to ${bucketName}:`, error);
    throw error;
  }
  return data.path;
};

const getPublicUrl = (bucketName: string, filePath: string) => {
  const { data } = supabase.storage.from(bucketName).getPublicUrl(filePath);
  return data.publicUrl;
};

const uploadProfilePicture = async (file: File): Promise<string> => {
  const filePath = `player_profiles/${file.name}`;
  const path = await uploadImage('cricket-dashboard', filePath, file);
  return getPublicUrl('cricket-dashboard', path);
};

const uploadCoverImage = async (file: File): Promise<string> => {
  const filePath = `player_covers/${file.name}`;
  const path = await uploadImage('cricket-dashboard', filePath, file);
  return getPublicUrl('cricket-dashboard', path);
};

const uploadTeamLogo = async (file: File): Promise<string> => {
  const filePath = `team_logos/${file.name}`;
  const path = await uploadImage('cricket-dashboard', filePath, file);
  return getPublicUrl('cricket-dashboard', path);
};

// Tournaments
export const fetchTournaments = async (): Promise<Tournament[]> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  // Fetch tournaments that the user has access to through profile_tournaments
  const { data: tournaments, error } = await supabase
    .from('profile_tournaments')
    .select(`
      role,
      tournaments (
        id,
        unique_id,
        name,
        location,
        start_date,
        logo_url,
        shadow_logo_color,
        created_at
      )
    `)
    .eq('profile_id', session.session.user.id);

  if (error) throw error;

  return tournaments.map((item: any) => {
    const tournament = item.tournaments;
    return {
      id: tournament.id,
      uniqueId: tournament.unique_id,
      name: tournament.name,
      location: tournament.location,
      startDate: tournament.start_date,
      logoUrl: tournament.logo_url || null,
      logoColor: tournament.shadow_logo_color ?? undefined,
      teams: [],
      createdAt: tournament.created_at,
      userRole: item.role as 'owner' | 'editor' | 'viewer',
    };
  });
};

export const createTournament = async (tournament: CreateTournamentPayload): Promise<Tournament> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  let logoUrl = null;
  if (tournament.logoFile) {
    const filePath = `tournament_logos/${tournament.logoFile.name}`;
    const path = await uploadImage('cricket-dashboard', filePath, tournament.logoFile);
    logoUrl = getPublicUrl('cricket-dashboard', path);
  }

  // Start a transaction to create tournament and profile relationship
  const { data, error } = await supabase
    .from('tournaments')
    .insert({
      name: tournament.name,
      location: tournament.location,
      start_date: tournament.startDate,
      unique_id: generateUniqueId(new Date(tournament.startDate)),
      user_id: session.session.user.id,
      logo_url: logoUrl,
      shadow_logo_color: tournament.logoColor ?? getRandomColor(),
    })
    .select()
    .single();

  if (error) throw error;

  // Create the profile_tournaments relationship
  const { error: profileError } = await supabase
    .from('profile_tournaments')
    .insert({
      profile_id: session.session.user.id,
      tournament_id: data.id,
      role: 'owner'
    });

  if (profileError) throw profileError;

  const tournamentData = data as unknown as {
    id: string;
    unique_id?: string;
    name: string;
    location: string;
    start_date: string;
    logo_url?: string;
    shadow_logo_color?: string;
  };
  return {
    id: tournamentData.id,
    uniqueId: tournamentData.unique_id,
    name: tournamentData.name,
    location: tournamentData.location,
    startDate: tournamentData.start_date,
    logoUrl: tournamentData.logo_url,
    logoColor: tournamentData.shadow_logo_color,
    teams: [],
    userRole: 'owner',
  };
};

export const updateTournament = async (tournament: UpdateTournamentPayload): Promise<Tournament> => {
  let logoUrl = undefined;
  if (tournament.logoFile) {
    const filePath = `tournament_logos/${tournament.logoFile.name}`;
    const uploadedPath = await uploadImage('cricket-dashboard', filePath, tournament.logoFile);
    logoUrl = getPublicUrl('cricket-dashboard', uploadedPath);
  } else if (tournament.logoFile === null) {
    logoUrl = null;
  }

  const { data, error } = await supabase
    .from('tournaments')
    .update({
      name: tournament.name,
      location: tournament.location,
      start_date: tournament.startDate,
      logo_url: logoUrl,
    })
    .eq('id', tournament.id)
    .select()
    .single();

  if (error) throw error;

  const tournamentData = data as unknown as {
    id: string;
    unique_id?: string;
    name: string;
    location: string;
    start_date: string;
    logo_url?: string;
    shadow_logo_color?: string;
  };
  return {
    id: tournamentData.id,
    uniqueId: tournamentData.unique_id,
    name: tournamentData.name,
    location: tournamentData.location,
    startDate: tournamentData.start_date,
    logoUrl: tournamentData.logo_url,
    logoColor: tournamentData.shadow_logo_color,
    teams: [],
  };
};

export const deleteTournament = async (tournamentId: string): Promise<void> => {
  // Check if there are teams under this tournament
  const { data: teams, error: teamsError } = await supabase
    .from('teams')
    .select('id')
    .eq('tournament_id', tournamentId);

  if (teamsError) throw teamsError;

  if (teams && teams.length > 0) {
    throw new Error('Cannot delete tournament. Please remove all teams first.');
  }

  // Delete profile_tournaments relationships first
  const { error: profileError } = await supabase
    .from('profile_tournaments')
    .delete()
    .eq('tournament_id', tournamentId);

  if (profileError) throw profileError;

  // Then delete the tournament
  const { error } = await supabase
    .from('tournaments')
    .delete()
    .eq('id', tournamentId);

  if (error) throw error;
};

// Teams
export const fetchTeams = async (): Promise<Team[]> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  // Fetch teams that the user has access to through profile_teams
  const { data: teams, error } = await supabase
    .from('profile_teams')
    .select(`
      role,
      teams (
        id,
        unique_id,
        name,
        short_code,
        color,
        tournament_id,
        logo_url,
        created_at,
        players (*)
      )
    `)
    .eq('profile_id', session.session.user.id);

  if (error) throw error;

  return teams.map((item: any) => {
    const team = item.teams;
    return {
      id: team.id,
      uniqueId: team.unique_id || undefined,
      name: team.name,
      shortCode: team.short_code || undefined,
      color: team.color || undefined,
      tournamentId: team.tournament_id || undefined,
      players: team.players || [],
      logoUrl: team.logo_url || undefined,
      userRole: item.role as 'owner' | 'editor' | 'viewer',
    };
  });
};

export const createTeam = async (team: CreateTeamPayload): Promise<Team> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  let logoUrl: string | undefined;
  if (team.logoFile) {
    logoUrl = await uploadTeamLogo(team.logoFile);
  }

  const { data, error } = await supabase
    .from('teams')
    .insert({
      name: team.name,
      short_code: team.shortCode,
      color: team.color,
      tournament_id: team.tournamentId,
      user_id: session.session.user.id,
      unique_id: generateUniqueId(new Date()),
      logo_url: logoUrl,
    })
    .select()
    .single();

  if (error) throw error;

  // Create the profile_teams relationship
  const { error: profileError } = await supabase
    .from('profile_teams')
    .insert({
      profile_id: session.session.user.id,
      team_id: data.id,
      role: 'owner'
    });

  if (profileError) throw profileError;

  const teamData = data as unknown as Tables<'teams'> & { logo_url?: string };

  return {
    id: teamData.id,
    uniqueId: teamData.unique_id || undefined,
    name: teamData.name,
    shortCode: teamData.short_code || undefined,
    color: teamData.color || undefined,
    tournamentId: teamData.tournament_id || undefined,
    players: [],
    logoUrl: teamData.logo_url || undefined,
    userRole: 'owner',
  };
};

export const updateTeam = async (team: UpdateTeamPayload): Promise<Team> => {
  let logoUrl: string | undefined = team.logoUrl;
  if (team.logoFile) {
    logoUrl = await uploadTeamLogo(team.logoFile);
  } else if (team.logoFile === null) {
    logoUrl = null;
  }

  const { data, error } = await supabase
    .from('teams')
    .update({
      name: team.name,
      short_code: team.shortCode,
      color: team.color,
      tournament_id: team.tournamentId,
      logo_url: logoUrl,
    })
    .eq('id', team.id)
    .select()
    .single();

  if (error) throw error;

  const teamData = data as unknown as Tables<'teams'> & { logo_url?: string };

  return {
    id: teamData.id,
    uniqueId: teamData.unique_id || undefined,
    name: teamData.name,
    shortCode: teamData.short_code || undefined,
    color: teamData.color || undefined,
    tournamentId: teamData.tournament_id || undefined,
    players: team.players || [],
    logoUrl: teamData.logo_url || undefined,
  };
};

export const deleteTeam = async (teamId: string): Promise<void> => {
  // First, remove all associations of this team in the player_teams table
   
  const { error: playerTeamsError } = await supabase
    .from('player_teams')
    .delete()
    .eq('team_id', teamId);

  if (playerTeamsError) {
    console.error("Error deleting player-team associations:", playerTeamsError);
    throw playerTeamsError;
  }

  // Delete profile_teams relationships
  const { error: profileError } = await supabase
    .from('profile_teams')
    .delete()
    .eq('team_id', teamId);

  if (profileError) {
    console.error("Error deleting profile-team associations:", profileError);
    throw profileError;
  }

  // Check if team is used in any matches
  const { data: matches, error: matchesError } = await supabase
    .from('matches')
    .select('id')
    .or(`team1_id.eq.${teamId},team2_id.eq.${teamId}`);

  if (matchesError) throw matchesError;

  if (matches && matches.length > 0) {
    throw new Error('Cannot delete team. Team is used in existing matches.');
  }

  // Then, delete the team itself
  const { error } = await supabase
    .from('teams')
    .delete()
    .eq('id', teamId);

  if (error) throw error;
};

// Players
export const fetchPlayers = async (): Promise<Player[]> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  // Fetch players that belong to teams managed by the current user
  // This uses the relationship: profile_teams -> teams -> player_teams -> players
  const { data: players, error } = await supabase
    .from('profile_teams')
    .select(`
      teams!inner (
        player_teams!inner (
          players (
            id,
            unique_id,
            name,
            role,
            is_captain,
            batting_style,
            batting_order,
            bowling_style,
            bowling_arm,
            bowling_type,
            keeping_style,
            fielding_position,
            fielding_style,
            injury_status,
            match_fitness,
            recent_form,
            career_runs,
            career_matches_played,
            career_innings_played,
            career_strike_rate,
            career_average,
            highest_score,
            not_outs,
            fours,
            sixes,
            career_wickets,
            career_overs_bowled,
            career_economy_rate,
            career_bowling_average,
            best_bowling_figures,
            maiden_overs,
            catches,
            run_outs,
            stumpings,
            date_of_birth,
            place_of_birth,
            nationality,
            height_cm,
            weight_kg,
            jersey_number,
            nickname,
            gender,
            country,
            profile_picture_url,
            cover_image_url,
            shadow_logo_color,
            created_at,
            updated_at
          )
        )
      )
    `)
    .eq('profile_id', session.session.user.id);

  if (error) throw error;

  // Flatten the nested structure and remove duplicates
  const playerMap = new Map<string, Player>();
  
  players.forEach((profileTeam: any) => {
    profileTeam.teams.player_teams.forEach((playerTeam: any) => {
      const player = playerTeam.players;
      if (player && !playerMap.has(player.id)) {
        const p = player;
        playerMap.set(player.id, {
          id: p.id,
          uniqueId: p.unique_id || undefined,
          name: p.name,
          role: p.role as Player['role'],
          teamIds: [], // Will be populated separately
          isCaptain: p.is_captain ?? undefined,
          battingStyle: p.batting_style as Player['battingStyle'] || undefined,
          battingOrder: p.batting_order as Player['battingOrder'] || undefined,
          bowlingStyle: p.bowling_style as Player['bowlingStyle'] || undefined,
          bowlingArm: p.bowling_arm as Player['bowlingArm'] || undefined,
          bowlingType: p.bowling_type as Player['bowlingType'] || undefined,
          keepingStyle: p.keeping_style as Player['keepingStyle'] || undefined,
          fieldingPosition: p.fielding_position || undefined,
          fieldingStyle: p.fielding_style as Player['fieldingStyle'] || undefined,
          injuryStatus: p.injury_status as Player['injuryStatus'] || undefined,
          matchFitness: p.match_fitness ?? undefined,
          recentForm: p.recent_form || undefined,
          careerRuns: p.career_runs ?? undefined,
          careerMatchesPlayed: p.career_matches_played ?? undefined,
          careerInningsPlayed: p.career_innings_played ?? undefined,
          careerStrikeRate: p.career_strike_rate ?? undefined,
          careerAverage: p.career_average ?? undefined,
          highestScore: p.highest_score ?? undefined,
          notOuts: p.not_outs ?? undefined,
          fours: p.fours ?? undefined,
          sixes: p.sixes ?? undefined,
          careerWickets: p.career_wickets ?? undefined,
          careerOversBowled: p.career_overs_bowled ?? undefined,
          careerEconomyRate: p.career_economy_rate ?? undefined,
          careerBowlingAverage: p.career_bowling_average ?? undefined,
          bestBowlingFigures: p.best_bowling_figures || undefined,
          maidenOvers: p.maiden_overs ?? undefined,
          catches: p.catches ?? undefined,
          runOuts: p.run_outs ?? undefined,
          stumpings: p.stumpings ?? undefined,
          dateOfBirth: p.date_of_birth || undefined,
          placeOfBirth: p.place_of_birth || undefined,
          nationality: p.nationality || undefined,
          heightCm: p.height_cm ?? undefined,
          weightKg: p.weight_kg ?? undefined,
          jerseyNumber: p.jersey_number ?? undefined,
          nickname: p.nickname || undefined,
          gender: p.gender as Player['gender'] || undefined,
          country: p.country || undefined,
          profilePictureUrl: p.profile_picture_url || null,
          coverImageUrl: p.cover_image_url || null,
          logoColor: p.shadow_logo_color || undefined,
          createdAt: p.created_at || undefined,
          updatedAt: p.updated_at || undefined,
        });
      }
    });
  });

  // Get team IDs for each player
  const playerIds = Array.from(playerMap.keys());
  if (playerIds.length > 0) {
    const { data: playerTeams, error: playerTeamsError } = await supabase
      .from('player_teams')
      .select('player_id, team_id')
      .in('player_id', playerIds);

    if (!playerTeamsError && playerTeams) {
      // Group team IDs by player
      const teamIdsByPlayer = new Map<string, string[]>();
      playerTeams.forEach((pt: any) => {
        if (!teamIdsByPlayer.has(pt.player_id)) {
          teamIdsByPlayer.set(pt.player_id, []);
        }
        teamIdsByPlayer.get(pt.player_id)!.push(pt.team_id);
      });

      // Update players with their team IDs
      playerMap.forEach((player, playerId) => {
        player.teamIds = teamIdsByPlayer.get(playerId) || [];
      });
    }
  }

  return Array.from(playerMap.values());
};

export const createPlayer = async (player: CreatePlayerPayload): Promise<Player> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;

  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  let profilePictureUrl = null;
  if (player.profilePictureFile) {
    profilePictureUrl = await uploadProfilePicture(player.profilePictureFile);
  }

  let coverImageUrl = null;
  if (player.coverImageFile) {
    coverImageUrl = await uploadCoverImage(player.coverImageFile);
  }

  const { data, error } = await supabase
    .from('players')
    .insert({
      name: player.name,
      first_name: player.first_name,
      middle_name: player.middle_name,
      last_name: player.last_name,
      role: player.role,
      is_captain: player.isCaptain ?? false,
      unique_id: generateUniqueId(new Date()),
      user_id: session.session.user.id,
      country: player.country,
      batting_style: player.battingStyle,
      bowling_style: player.bowlingStyle,
      date_of_birth: player.dateOfBirth,
      profile_picture_url: profilePictureUrl,
      cover_image_url: coverImageUrl,
      place_of_birth: player.placeOfBirth,
      height_cm: player.heightCm,
      weight_kg: player.weightKg,
      jersey_number: player.jerseyNumber,
      nickname: player.nickname,
      gender: player.gender,
      batting_order: player.battingOrder,
      bowling_arm: player.bowlingArm,
      bowling_type: player.bowlingType,
      keeping_style: player.keepingStyle,
      fielding_position: player.fieldingPosition,
      fielding_style: player.fieldingStyle,
      injury_status: player.injuryStatus,
      match_fitness: player.matchFitness,
      recent_form: player.recentForm,
      career_runs: player.careerRuns,
      career_matches_played: player.careerMatchesPlayed,
      career_innings_played: player.careerInningsPlayed,
      career_strike_rate: player.careerStrikeRate,
      career_average: player.careerAverage,
      highest_score: player.highestScore,
      not_outs: player.notOuts,
      fours: player.fours,
      sixes: player.sixes,
      career_wickets: player.careerWickets,
      career_overs_bowled: player.careerOversBowled,
      career_economy_rate: player.careerEconomyRate,
      career_bowling_average: player.careerBowlingAverage,
      best_bowling_figures: player.bestBowlingFigures,
      maiden_overs: player.maidenOvers,
      catches: player.catches,
      run_outs: player.runOuts,
      stumpings: player.stumpings,
      shadow_logo_color: player.logoColor || getRandomColor(),
      nationality: player.nationality,
    })
    .select()
    .single();

  if (error) throw error;

  const playerData = data as unknown as Tables<'players'> & {
    batting_style?: string;
    bowling_style?: string;
    keeping_style?: string;
    fielding_style?: string;
    injury_status?: 'fit' | 'injured' | 'recovering';
    match_fitness?: number;
    recent_form?: string;
    career_runs?: number;
    career_matches_played?: number;
    career_innings_played?: number;
    career_strike_rate?: number;
    career_average?: number;
    highest_score?: number;
    not_outs?: number;
    fours?: number;
    sixes?: number;
    career_wickets?: number;
    career_overs_bowled?: number;
    career_economy_rate?: number;
    career_bowling_average?: number;
    best_bowling_figures?: string;
    maiden_overs?: number;
    catches?: number;
    run_outs?: number;
    stumpings?: number;
    date_of_birth?: string;
    place_of_birth?: string;
    nationality?: string;
    height_cm?: number;
    weight_kg?: number;
    jersey_number?: number;
    nickname?: string;
    gender?: 'male' | 'female' | 'other';
    country?: string;
    profile_picture_url?: string | null;
    cover_image_url?: string | null;
    shadow_logo_color?: string;
    created_at?: string;
    updated_at?: string;
    batting_order?: string;
    bowling_arm?: string;
    bowling_type?: string;
  };

  // Handle team associations
  if (player.teamIds && player.teamIds.length > 0) {
    const playerTeamInserts = player.teamIds.map(teamId => ({
      player_id: playerData.id,
      team_id: teamId
    }));
     
    const { error: playerTeamError } = await supabase
      .from('player_teams')
      .insert(playerTeamInserts);

    if (playerTeamError) {
      console.error("Error associating player with teams:", playerTeamError);
      throw playerTeamError;
    }
  }
  
  return {
    id: playerData.id,
    uniqueId: playerData.unique_id || undefined,
    name: playerData.name,
    role: playerData.role as Player['role'],
    teamIds: player.teamIds || [],
    isCaptain: playerData.is_captain ?? undefined,
    battingStyle: playerData.batting_style as Player['battingStyle'] || undefined,
    battingOrder: playerData.batting_order as Player['battingOrder'] || undefined,
    bowlingStyle: playerData.bowling_style as Player['bowlingStyle'] || undefined,
    bowlingArm: playerData.bowling_arm as Player['bowlingArm'] || undefined,
    bowlingType: playerData.bowling_type as Player['bowlingType'] || undefined,
    keepingStyle: playerData.keeping_style as Player['keepingStyle'] || undefined,
    fieldingPosition: (playerData as any).fielding_position || undefined,
    fieldingStyle: playerData.fielding_style as Player['fieldingStyle'] || undefined,
    injuryStatus: playerData.injury_status as Player['injuryStatus'] || undefined,
    matchFitness: playerData.match_fitness ?? undefined,
    recentForm: playerData.recent_form || undefined,
    careerRuns: playerData.career_runs ?? undefined,
    careerMatchesPlayed: playerData.career_matches_played ?? undefined,
    careerInningsPlayed: playerData.career_innings_played ?? undefined,
    careerStrikeRate: playerData.career_strike_rate ?? undefined,
    careerAverage: playerData.career_average ?? undefined,
    highestScore: playerData.highest_score ?? undefined,
    notOuts: playerData.not_outs ?? undefined,
    fours: playerData.fours ?? undefined,
    sixes: playerData.sixes ?? undefined,
    careerWickets: playerData.career_wickets ?? undefined,
    careerOversBowled: playerData.career_overs_bowled ?? undefined,
    careerEconomyRate: playerData.career_economy_rate ?? undefined,
    careerBowlingAverage: playerData.career_bowling_average ?? undefined,
    bestBowlingFigures: playerData.best_bowling_figures || undefined,
    maidenOvers: playerData.maiden_overs ?? undefined,
    catches: playerData.catches ?? undefined,
    runOuts: playerData.run_outs ?? undefined,
    stumpings: playerData.stumpings ?? undefined,
    dateOfBirth: playerData.date_of_birth || undefined,
    placeOfBirth: playerData.place_of_birth || undefined,
    nationality: playerData.nationality || undefined,
    heightCm: playerData.height_cm ?? undefined,
    weightKg: playerData.weight_kg ?? undefined,
    jerseyNumber: playerData.jersey_number ?? undefined,
    nickname: playerData.nickname || undefined,
    gender: playerData.gender as Player['gender'] || undefined,
    country: playerData.country || undefined,
    profilePictureUrl: playerData.profile_picture_url || null,
    coverImageUrl: playerData.cover_image_url || null,
    logoColor: playerData.shadow_logo_color || undefined,
    createdAt: playerData.created_at || undefined,
    updatedAt: playerData.updated_at || undefined,
  };
};

export const updatePlayer = async (player: UpdatePlayerPayload): Promise<Player> => {
  let profilePictureUrl = undefined;
  if (player.profilePictureFile) {
    const filePath = `player_profiles/${player.profilePictureFile.name}`;
    const path = await uploadImage('cricket-dashboard', filePath, player.profilePictureFile);
    profilePictureUrl = getPublicUrl('cricket-dashboard', path);
  } else if (player.profilePictureFile === null) {
    profilePictureUrl = null;
  }

  let coverImageUrl = undefined;
  if (player.coverImageFile) {
    const filePath = `player_covers/${player.coverImageFile.name}`;
    const path = await uploadImage('cricket-dashboard', filePath, player.coverImageFile);
    coverImageUrl = getPublicUrl('cricket-dashboard', path);
  } else if (player.coverImageFile === null) {
    coverImageUrl = null;
  }

  const updateData: Record<string, unknown> = {};
  if (player.name !== undefined) updateData.name = player.name;
  if (player.role !== undefined) updateData.role = player.role;
  if (player.battingStyle !== undefined) updateData.batting_style = player.battingStyle;
  if (player.bowlingStyle !== undefined) updateData.bowling_style = player.bowlingStyle;
  if (player.dateOfBirth !== undefined) updateData.date_of_birth = player.dateOfBirth;

  if (player.teamIds !== undefined) {
    // Delete existing associations
     
    const { error: deleteError } = await supabase
      .from('player_teams')
      .delete()
      .eq('player_id', player.id);

    if (deleteError) {
      console.error("Error deleting existing player_teams:", deleteError);
      throw deleteError;
    }

    // Insert new associations
    if (player.teamIds.length > 0) {
      const playerTeamsData = player.teamIds.map(teamId => ({
        player_id: player.id,
        team_id: teamId,
      }));
       
      const { error: insertError } = await supabase
        .from('player_teams')
        .insert(playerTeamsData);

      if (insertError) {
        console.error("Error inserting new player_teams:", insertError);
        throw insertError;
      }
    }
  }
  if (profilePictureUrl !== undefined) updateData.profile_picture_url = profilePictureUrl;
  if (coverImageUrl !== undefined) updateData.cover_image_url = coverImageUrl;
  if (player.logoColor !== undefined) updateData.shadow_logo_color = player.logoColor;
  if (player.isCaptain !== undefined) updateData.is_captain = player.isCaptain;
  if (player.uniqueId !== undefined) updateData.unique_id = player.uniqueId;
  if (player.placeOfBirth !== undefined) updateData.place_of_birth = player.placeOfBirth;
  if (player.heightCm !== undefined) updateData.height_cm = player.heightCm;
  if (player.weightKg !== undefined) updateData.weight_kg = player.weightKg;
  if (player.jerseyNumber !== undefined) updateData.jersey_number = player.jerseyNumber;
  if (player.nickname !== undefined) updateData.nickname = player.nickname;
  if (player.gender !== undefined) updateData.gender = player.gender;
  if (player.battingOrder !== undefined) updateData.batting_order = player.battingOrder;
  if (player.bowlingArm !== undefined) updateData.bowling_arm = player.bowlingArm;
  if (player.bowlingType !== undefined) updateData.bowling_type = player.bowlingType;
  if (player.keepingStyle !== undefined) updateData.keeping_style = player.keepingStyle;
  if (player.fieldingPosition !== undefined) updateData.fielding_position = player.fieldingPosition;
  if (player.fieldingStyle !== undefined) updateData.fielding_style = player.fieldingStyle;
  if (player.injuryStatus !== undefined) updateData.injury_status = player.injuryStatus;
  if (player.matchFitness !== undefined) updateData.match_fitness = player.matchFitness;
  if (player.recentForm !== undefined) updateData.recent_form = player.recentForm;
  if (player.careerRuns !== undefined) updateData.career_runs = player.careerRuns;
  if (player.careerMatchesPlayed !== undefined) updateData.career_matches_played = player.careerMatchesPlayed;
  if (player.careerInningsPlayed !== undefined) updateData.career_innings_played = player.careerInningsPlayed;
  if (player.careerStrikeRate !== undefined) updateData.career_strike_rate = player.careerStrikeRate;
  if (player.careerAverage !== undefined) updateData.career_average = player.careerAverage;
  if (player.highestScore !== undefined) updateData.highest_score = player.highestScore;
  if (player.notOuts !== undefined) updateData.not_outs = player.notOuts;
  if (player.fours !== undefined) updateData.fours = player.fours;
  if (player.sixes !== undefined) updateData.sixes = player.sixes;
  if (player.careerWickets !== undefined) updateData.career_wickets = player.careerWickets;
  if (player.careerOversBowled !== undefined) updateData.career_overs_bowled = player.careerOversBowled;
  if (player.careerEconomyRate !== undefined) updateData.career_economy_rate = player.careerEconomyRate;
  if (player.careerBowlingAverage !== undefined) updateData.career_bowling_average = player.careerBowlingAverage;
  if (player.bestBowlingFigures !== undefined) updateData.best_bowling_figures = player.bestBowlingFigures;
  if (player.maidenOvers !== undefined) updateData.maiden_overs = player.maidenOvers;
  if (player.catches !== undefined) updateData.catches = player.catches;
  if (player.runOuts !== undefined) updateData.run_outs = player.runOuts;
  if (player.stumpings !== undefined) updateData.stumpings = player.stumpings;
  if (player.nationality !== undefined) updateData.nationality = player.nationality;

  const { error: updateError } = await supabase
    .from('players')
    .update(updateData)
    .eq('id', player.id);

  if (updateError) {
    console.error("Error updating player:", updateError);
    throw updateError;
  }

  return getPlayer(player.id);
};

export const deletePlayer = async (playerId: string): Promise<void> => {
  const { error } = await supabase
    .from('players')
    .delete()
    .eq('id', playerId);

  if (error) throw error;
};

export const getPlayer = async (playerId: string): Promise<Player | null> => {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('id', playerId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // No player found
    }
    throw error;
  }

  const d = data as unknown as {
    id: string;
    unique_id?: string;
    name: string;
    role: string;
    team_id?: string[];
    is_captain?: boolean;
    batting_style?: string;
    bowling_style?: string;
    keeping_style?: string;
    fielding_style?: string;
    injury_status?: 'fit' | 'injured' | 'recovering';
    match_fitness?: number;
    recent_form?: string;
    career_runs?: number;
    career_matches_played?: number;
    career_innings_played?: number;
    career_strike_rate?: number;
    career_average?: number;
    highest_score?: number;
    not_outs?: number;
    fours?: number;
    sixes?: number;
    career_wickets?: number;
    career_overs_bowled?: number;
    career_economy_rate?: number;
    career_bowling_average?: number;
    best_bowling_figures?: string;
    maiden_overs?: number;
    catches?: number;
    run_outs?: number;
    stumpings?: number;
    date_of_birth?: string;
    place_of_birth?: string;
    nationality?: string;
    height_cm?: number;
    weight_kg?: number;
    jersey_number?: number;
    nickname?: string;
    gender?: 'male' | 'female' | 'other';
    country?: string;
    profile_picture_url?: string | null;
    cover_image_url?: string | null;
    shadow_logo_color?: string;
    created_at?: string;
    updated_at?: string;
    batting_order?: string;
    bowling_arm?: string;
    bowling_type?: string;
  };
  return {
    id: d.id,
    uniqueId: d.unique_id || undefined,
    name: d.name,
    role: d.role as Player['role'],
    teamIds: d.team_id || [],
    isCaptain: d.is_captain ?? undefined,
    battingStyle: d.batting_style as Player['battingStyle'] || undefined,
    battingOrder: d.batting_order as Player['battingOrder'] || undefined,
    bowlingStyle: d.bowling_style as Player['bowlingStyle'] || undefined,
    bowlingArm: d.bowling_arm as Player['bowlingArm'] || undefined,
    bowlingType: d.bowling_type as Player['bowlingType'] || undefined,
    keepingStyle: d.keeping_style as Player['keepingStyle'] || undefined,
    fieldingPosition: (d as any).fielding_position || undefined,
    fieldingStyle: d.fielding_style as Player['fieldingStyle'] || undefined,
    injuryStatus: d.injury_status as Player['injuryStatus'] || undefined,
    matchFitness: d.match_fitness ?? undefined,
    recentForm: d.recent_form || undefined,
    careerRuns: d.career_runs ?? undefined,
    careerMatchesPlayed: d.career_matches_played ?? undefined,
    careerInningsPlayed: d.career_innings_played ?? undefined,
    careerStrikeRate: d.career_strike_rate ?? undefined,
    careerAverage: d.career_average ?? undefined,
    highestScore: d.highest_score ?? undefined,
    notOuts: d.not_outs ?? undefined,
    fours: d.fours ?? undefined,
    sixes: d.sixes ?? undefined,
    careerWickets: d.career_wickets ?? undefined,
    careerOversBowled: d.career_overs_bowled ?? undefined,
    careerEconomyRate: d.career_economy_rate ?? undefined,
    careerBowlingAverage: d.career_bowling_average ?? undefined,
    bestBowlingFigures: d.best_bowling_figures || undefined,
    maidenOvers: d.maiden_overs ?? undefined,
    catches: d.catches ?? undefined,
    runOuts: d.run_outs ?? undefined,
    stumpings: d.stumpings ?? undefined,
    dateOfBirth: d.date_of_birth || undefined,
    placeOfBirth: d.place_of_birth || undefined,
    nationality: d.nationality || undefined,
    heightCm: d.height_cm ?? undefined,
    weightKg: d.weight_kg ?? undefined,
    jerseyNumber: d.jersey_number ?? undefined,
    nickname: d.nickname || undefined,
    gender: d.gender as Player['gender'] || undefined,
    country: d.country || undefined,
    profilePictureUrl: d.profile_picture_url || null,
    coverImageUrl: d.cover_image_url || null,
    logoColor: d.shadow_logo_color || undefined,
    createdAt: d.created_at || undefined,
    updatedAt: d.updated_at || undefined,
  };
};

// Matches
export const fetchMatches = async (): Promise<Match[]> => {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!sessionData?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  const userId = sessionData.session.user.id;

  // First, get all tournaments and teams the user has access to
  const [tournamentAccess, teamAccess] = await Promise.all([
    supabase
      .from('profile_tournaments')
      .select('tournament_id')
      .eq('profile_id', userId),
    supabase
      .from('profile_teams')
      .select('team_id')
      .eq('profile_id', userId)
  ]);

  if (tournamentAccess.error) throw tournamentAccess.error;
  if (teamAccess.error) throw teamAccess.error;

  const accessibleTournamentIds = tournamentAccess.data?.map(t => t.tournament_id) || [];
  const accessibleTeamIds = teamAccess.data?.map(t => t.team_id) || [];

  // If user has no access to any tournaments or teams, return empty array
  if (accessibleTournamentIds.length === 0 && accessibleTeamIds.length === 0) {
    return [];
  }

  // Build the OR condition
  const conditions = [];
  if (accessibleTournamentIds.length > 0) {
    conditions.push(`tournament_id.in.(${accessibleTournamentIds.join(',')})`);
  }
  if (accessibleTeamIds.length > 0) {
    conditions.push(`team1_id.in.(${accessibleTeamIds.join(',')})`);
    conditions.push(`team2_id.in.(${accessibleTeamIds.join(',')})`);
  }

  // Fetch matches where user has access to tournament or either team
  const { data: matches, error } = await supabase
    .from('matches')
    .select('*')
    .or(conditions.join(','));

  if (error) {
    console.error('Error fetching matches:', error);
    throw error;
  }

  // --- PR-style diff: fetch team names separately and attach to matches ---
  // 1. Collect all unique team IDs from matches
  const teamIds = [
    ...new Set(
      (matches ?? []).flatMap(m => [m.team1_id, m.team2_id]).filter(Boolean)
    )
  ];

  // 2. Fetch team names for those IDs
  const { data: teams, error: teamError } = await supabase
    .from('teams')
    .select('id, name')
    .in('id', teamIds);
  if (teamError) throw teamError;

  // 3. Build a lookup map
  const teamNameMap = Object.fromEntries((teams ?? []).map(t => [t.id, t.name]));

  // 4. Attach team names to matches
  return (matches as unknown as MatchRow[]).map((match: MatchRow) => ({
    id: match.id,
    uniqueId: match.unique_id || undefined,
    tournamentId: match.tournament_id,
    team1Id: match.team1_id,
    team2Id: match.team2_id,
    team1Name: teamNameMap[match.team1_id] || 'Unknown',
    team2Name: teamNameMap[match.team2_id] || 'Unknown',
    matchDate: match.date || '',
    city: match.city,
    status: match.status as 'upcoming' | 'live' | 'completed',
    team1Score: {
      runs: match.team1_score_runs || 0,
      wickets: match.team1_score_wickets || 0,
      overs: match.team1_score_overs || 0,
      balls: match.team1_score_balls || 0,
    },
    team2Score: {
      runs: match.team2_score_runs || 0,
      wickets: match.team2_score_wickets || 0,
      overs: match.team2_score_overs || 0,
      balls: match.team2_score_balls || 0,
    },
    currentInnings: (match.current_innings === 2 ? 2 : 1) as 1 | 2,
    currentBattingTeam: match.current_batting_team_id || match.team1_id,
    matchType: match.match_type,
    totalOvers: match.total_overs,
    ballsPerOver: match.balls_per_over,
    totalWickets: match.total_wickets || 10,
    totalInnings: match.total_innings || 2,
    tossWinnerId: match.toss_winner_id,
    tossSelectedBattingTeam: match.toss_selected_batting_team,
    team1CaptainId: match.team1_captain_id || undefined,
    team2CaptainId: match.team2_captain_id || undefined,
    team1ViceCaptainId: match.team1_vice_captain_id || undefined,
    team2ViceCaptainId: match.team2_vice_captain_id || undefined,
    currentFielderId: match.current_fielder_id || undefined,
    innings: match.innings_data ? JSON.parse(JSON.stringify(match.innings_data)) : [],
    inningsEndReason: match.innings_end_reason || undefined,
  }));
};

export const createMatch = async (match: Match): Promise<Match> => {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!sessionData?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  const matchData: Omit<Tables<'matches'>, 'id' | 'created_at' | 'user_id' | 'unique_id'> & { user_id: string, unique_id: string } = {
    tournament_id: match.tournamentId,
    team1_id: match.team1Id,
    team2_id: match.team2Id,
    date: match.matchDate,
    city: match.city,
    ground: match.ground,
    status: match.status,
    team1_score_runs: match.team1Score.runs,
    team1_score_wickets: match.team1Score.wickets,
    team1_score_overs: match.team1Score.overs,
    team1_score_balls: match.team1Score.balls,
    team2_score_runs: match.team2Score.runs,
    team2_score_wickets: match.team2Score.wickets,
    team2_score_overs: match.team2Score.overs,
    team2_score_balls: match.team2Score.balls,
    current_innings: match.currentInnings,
    current_batting_team_id: match.currentBattingTeam,
    match_type: match.matchType,
    total_overs: match.totalOvers,
    balls_per_over: match.ballsPerOver,
    total_wickets: match.totalWickets,
    total_innings: match.totalInnings,
    team1_captain_id: match.team1CaptainId,
    team2_captain_id: match.team2CaptainId,
    team1_vice_captain_id: match.team1ViceCaptainId,
    team2_vice_captain_id: match.team2ViceCaptainId,
    current_fielder_id: match.currentFielderId,
    innings_data: match.innings ? JSON.stringify(match.innings) : null,
    innings_end_reason: match.inningsEndReason,
    toss_winner_id: match.tossWinnerId,
    toss_selected_batting_team: match.tossSelectedBattingTeam,
    user_id: sessionData.session.user.id,
    unique_id: generateUniqueId(new Date(match.matchDate)),
  };

  const { data, error } = await supabase
    .from('matches')
    .insert(matchData)
    .select()
    .single();

  if (error) {
    console.error("Error creating match in Supabase:", error);
    throw error;
  }

  // Insert initial match_state row
  const { error: msError } = await supabase
    .from('match_state')
    .insert({
      match_id: data.id,
      current_over: 0,
      current_ball: 0,
      user_id: sessionData.session.user.id,
    });
  if (msError) {
    console.error('Error inserting initial match_state:', msError);
    // Don't throw, allow match creation to succeed
  }

  const matchRow = data as MatchRow;

  return {
    id: matchRow.id,
    uniqueId: matchRow.unique_id || undefined,
    tournamentId: matchRow.tournament_id,
    team1Id: matchRow.team1_id,
    team2Id: matchRow.team2_id,
    matchDate: matchRow.date,
    city: matchRow.city,
    status: matchRow.status as 'upcoming' | 'live' | 'completed',
    team1Score: {
      runs: matchRow.team1_score_runs || 0,
      wickets: matchRow.team1_score_wickets || 0,
      overs: matchRow.team1_score_overs || 0,
      balls: matchRow.team1_score_balls || 0,
    },
    team2Score: {
      runs: matchRow.team2_score_runs || 0,
      wickets: matchRow.team2_score_wickets || 0,
      overs: matchRow.team2_score_overs || 0,
      balls: matchRow.team2_score_balls || 0,
    },
    currentInnings: (matchRow.current_innings || 1) as 1 | 2,
    currentBattingTeam: matchRow.current_batting_team_id || '',
    matchType: matchRow.match_type,
    totalOvers: matchRow.total_overs || undefined,
    ballsPerOver: matchRow.balls_per_over || undefined,
    totalWickets: matchRow.total_wickets || undefined,
    totalInnings: matchRow.total_innings || undefined,
    team1CaptainId: matchRow.team1_captain_id || undefined,
    team2CaptainId: matchRow.team2_captain_id || undefined,
    team1ViceCaptainId: matchRow.team1_vice_captain_id || undefined,
    team2ViceCaptainId: matchRow.team2_vice_captain_id || undefined,
    currentFielderId: matchRow.current_fielder_id || undefined,
    innings: matchRow.innings_data ? JSON.parse(matchRow.innings_data as string) : [],
    inningsEndReason: matchRow.innings_end_reason || undefined,
    tossWinnerId: matchRow.toss_winner_id || undefined,
    tossSelectedBattingTeam: matchRow.toss_selected_batting_team || undefined,
  };
};

export const updateMatch = async (match: Match): Promise<Match> => {
  const { data, error } = await supabase
    .from('matches')
    .update({
      tournament_id: match.tournamentId,
      team1_id: match.team1Id,
      team2_id: match.team2Id,
      date: match.matchDate,
      city: match.city,
      status: match.status,
      match_type: match.matchType,
      total_overs: match.totalOvers,
      balls_per_over: match.ballsPerOver,
      total_innings: match.totalInnings,
      current_innings: match.currentInnings,
      current_batting_team_id: match.currentBattingTeam,
      toss_winner_id: match.tossWinnerId,
      toss_selected_batting_team: match.tossSelectedBattingTeam,
      current_fielder_id: match.currentFielderId || null,
      innings_data: match.innings ? JSON.stringify(match.innings) : null,
      innings_end_reason: match.inningsEndReason,
      team1_captain_id: match.team1CaptainId,
      team2_captain_id: match.team2CaptainId,
      team1_vice_captain_id: match.team1ViceCaptainId,
      team2_vice_captain_id: match.team2ViceCaptainId,
    })
    .eq('id', match.id)
    .select()
    .single<MatchRow>();

  if (error) throw error;

  return {
    id: data.id,
    uniqueId: data.unique_id,
    tournamentId: data.tournament_id,
    team1Id: data.team1_id,
    team2Id: data.team2_id,
    matchDate: data.date || '',
    city: data.city,
    status: data.status as 'upcoming' | 'live' | 'completed',
    team1Score: match.team1Score,
    team2Score: match.team2Score,
    currentInnings: data.current_innings,
    currentBattingTeam: data.current_batting_team_id || data.team1_id,
    matchType: data.match_type,
    totalOvers: data.total_overs,
    ballsPerOver: data.balls_per_over,
    totalWickets: data.total_wickets || 10,
    totalInnings: data.total_innings || 2,
    tossWinnerId: data.toss_winner_id,
    tossSelectedBattingTeam: data.toss_selected_batting_team,
    team1CaptainId: data.team1_captain_id || undefined,
    team2CaptainId: data.team2_captain_id || undefined,
    team1ViceCaptainId: data.team1_vice_captain_id || undefined,
    team2ViceCaptainId: data.team2_vice_captain_id || undefined,
    currentFielderId: data.current_fielder_id,
    innings: match.innings,
    inningsEndReason: data.innings_end_reason
  } as Match;
};

export const deleteMatch = async (matchId: string): Promise<void> => {
  // First delete all wickets for this match
  const { error: wicketsError } = await supabase
    .from('wickets')
    .delete()
    .eq('match_id', matchId);

  if (wicketsError) throw wicketsError;

  const { error } = await supabase
    .from('matches')
    .delete()
    .eq('id', matchId);

  if (error) throw error;
};

export const updateMatchStatus = async (matchId: string, status: 'upcoming' | 'live' | 'completed'): Promise<void> => {
  const { error } = await supabase
    .from('matches')
    .update({ status })
    .eq('id', matchId);

  if (error) throw error;
};

export const updateMatchScore = async (
  matchId: string,
  teamId: string,
  score: {
    runs: number;
    wickets: number;
    overs: number;
    balls: number;
  }
): Promise<void> => {
  // First fetch the match to determine which team we're updating
  const { data: match, error: fetchError } = await supabase
    .from('matches')
    .select('team1_id, team2_id')
    .eq('id', matchId)
    .single();

  if (fetchError) throw fetchError;

  const isTeam1 = teamId === match.team1_id;
  
  const updateData = isTeam1
    ? {
        team1_score_runs: score.runs,
        team1_score_wickets: score.wickets,
        team1_score_overs: score.overs,
        team1_score_balls: score.balls,
      }
    : {
        team2_score_runs: score.runs,
        team2_score_wickets: score.wickets,
        team2_score_overs: score.overs,
        team2_score_balls: score.balls,
      };

  const { error } = await supabase
    .from('matches')
    .update(updateData)
    .eq('id', matchId);

  if (error) throw error;
};

export const updateMatchInnings = async (matchId: string, innings: InningsData[], currentBattingTeam: string, currentInnings: number, currentFielderId?: string): Promise<void> => {
  const { error } = await supabase
    .from('matches')
    .update({
      innings_data: JSON.stringify(innings),
      current_batting_team_id: currentBattingTeam,
      current_innings: currentInnings,
      current_fielder_id: currentFielderId || null,
    })
    .eq('id', matchId);

  if (error) throw error;
};

// Wickets
export const createWicket = async (wicket: Wicket): Promise<Wicket> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('wickets')
    .insert({
      id: wicket.id,
      match_id: wicket.matchId,
      player_id: wicket.playerId,
      bowler_id: wicket.bowlerId,
      fielder_id: wicket.fielderId,
      wicket_type: wicket.wicketType,
      run_out_assist_type: wicket.runOutAssistType,
      innings: wicket.innings,
      over_number: wicket.overNumber,
      runs_scored: wicket.runsScored,
      user_id: session.session.user.id,
      review_used: wicket.reviewUsed,
      review_result: wicket.reviewResult,
      dismissed_batter_id: wicket.dismissedBatterId,
    })
    .select()
    .single();

  if (error) throw error;

  return {
    id: data.id,
    matchId: data.match_id,
    playerId: data.player_id,
    bowlerId: data.bowler_id,
    fielderId: data.fielder_id,
    wicketType: data.wicket_type,
    runOutAssistType: (data as any).run_out_assist_type,
    innings: data.innings,
    overNumber: data.over_number,
    ballNumber: data.ball_number,
    runsScored: data.runs_scored,
    reviewUsed: (data as any).review_used,
    reviewResult: (data as any).review_result,
    dismissedBatterId: data.dismissed_batter_id,
  };
};

export const fetchWickets = async (matchId: string): Promise<Wicket[]> => {
  const { data, error } = await supabase
    .from('wickets')
    .select('*')
    .eq('match_id', matchId)
    .order('created_at', { ascending: true });

  if (error) throw error;

  return data.map(wicket => ({
    id: wicket.id,
    matchId: wicket.match_id,
    playerId: wicket.player_id,
    bowlerId: wicket.bowler_id,
    fielderId: wicket.fielder_id,
    wicketType: wicket.wicket_type,
    runOutAssistType: (wicket as any).run_out_assist_type,
    innings: wicket.innings,
    overNumber: wicket.over_number,
    ballNumber: wicket.ball_number,
    runsScored: wicket.runs_scored,
    reviewUsed: (wicket as any).review_used,
    reviewResult: (wicket as any).review_result,
    dismissedBatterId: wicket.dismissed_batter_id,
  }));
};

export const deleteWicket = async (wicketId: string): Promise<void> => {
  const { error } = await supabase
    .from('wickets')
    .delete()
    .eq('id', wicketId);

  if (error) throw error;
};

export const saveScoreHistory = async (matchId: string, entry: ScoreEntry): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Save the ball event
  const { error } = await supabase
    .from('score_history')
    .upsert({
      id: entry.id,
      match_id: matchId,
      description: entry.description,
      runs: entry.runs,
      ball_runs: entry.ballRuns,
      extras: entry.extras,
      extra_type: entry.extraType,
      ball_count: entry.ballCount,
      bowler_runs: entry.bowlerRuns,
      batsman_runs: entry.batsmanRuns,
      wicket_id: entry.wicket?.id,
      is_free_hit: entry.isFreeHit,
      bowler_id: entry.bowlerId,
      striker_id: entry.strikerId,
      non_striker_id: entry.nonStrikerId,
      over_number: entry.overNumber,
      ball_number: entry.ballNumber,
      ball_direction: entry.ballDirection,
      ball_angle_deg: entry.ballAngleDeg,
      ball_distance_m: entry.ballDistanceM,
      fielder_id: entry.fielderId,
      fielding_position: entry.fieldingPosition,
      batting_team_id: entry.battingTeamId,
      user_id: user.id
    });

  if (error) {
    console.error('Error saving score history:', error);
    throw error;
  }

  // Upsert match_state for live roles
  const { error: msError } = await supabase
    .from('match_state')
    .upsert({
      match_id: matchId,
      current_striker_id: entry.strikerId,
      current_non_striker_id: entry.nonStrikerId,
      current_bowler_id: entry.bowlerId,
      current_over: entry.overNumber,
      current_ball: entry.ballNumber,
      user_id: user.id,
      updated_at: new Date().toISOString(),
    }, { onConflict: ['match_id'] });

  if (msError) {
    console.error('Error upserting match_state:', msError);
    throw msError;
  }
};

export const loadScoreHistory = async (matchId: string): Promise<ScoreEntry[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('score_history')
    .select('*')
    .eq('match_id', matchId)
    .eq('user_id', user.id);

  if (error) throw error;

  return (data as ScoreHistoryRow[])?.map((entry: ScoreHistoryRow) => ({
    id: entry.id,
    runs: entry.runs || 0,
    ballRuns: entry.ball_runs || 0,
    extras: entry.extras || 0,
    extraType: entry.extra_type || '',
    ballCount: entry.ball_count || 0,
    bowlerRuns: entry.bowler_runs || 0,
    batsmanRuns: entry.batsman_runs || 0,
    wicket: entry.wicket_id ? {
      id: entry.wicket_id as string,
      matchId: entry.match_id,
      playerId: '',
      bowlerId: '',
      wicketType: '',
      innings: entry.innings,
      overNumber: entry.over_number,
      ballNumber: entry.ball_number,
      runsScored: entry.runs || 0,
    } : undefined,
    timestamp: new Date(entry.created_at || 0).getTime(),
    description: entry.description || '',
    ballNumber: entry.ball_number || 0,
    overNumber: entry.over_number || 0,
    battingStats: [],
    bowlingStats: [],
    isFreeHit: entry.is_free_hit || false,
    bowlerId: entry.bowler_id,
    strikerId: entry.striker_id,
    nonStrikerId: entry.non_striker_id,
    ballDirection: entry.ball_direction,
    ballAngleDeg: entry.ball_angle_deg,
    ballDistanceM: entry.ball_distance_m,
    fielderId: entry.fielder_id,
    fieldingPosition: entry.fielding_position,
    battingTeamId: entry.batting_team_id,
  })) || [];
};

// New functions for enhanced tables

// Innings
export const createInnings = async (innings: {
  matchId: string;
  inningsNumber: number;
  battingTeamId: string;
  bowlingTeamId: string;
  startTime?: string;
  endTime?: string;
  battingCaptainId?: string;
  bowlingCaptainId?: string;
  inningStatus?: 'In Progress' | 'Completed' | 'Abandoned';
  isPowerplayApplied?: boolean;
  powerplayStartOver?: number;
  powerplayEndOver?: number;
  note?: string;
}): Promise<Innings> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('innings')
    .insert({
      match_id: innings.matchId,
      innings_number: innings.inningsNumber,
      batting_team_id: innings.battingTeamId,
      bowling_team_id: innings.bowlingTeamId,
      start_time: innings.startTime,
      end_time: innings.endTime,
      user_id: session.session.user.id,
      batting_captain_id: innings.battingCaptainId,
      bowling_captain_id: innings.bowlingCaptainId,
      inning_status: innings.inningStatus || 'In Progress',
      is_powerplay_applied: innings.isPowerplayApplied || false,
      powerplay_start_over: innings.powerplayStartOver,
      powerplay_end_over: innings.powerplayEndOver,
      note: innings.note
    })
    .select()
    .single();

  if (error) throw error;

  return {
    id: data.id,
    matchId: data.match_id,
    inningsNumber: data.innings_number,
    battingTeamId: data.batting_team_id,
    bowlingTeamId: data.bowling_team_id,
    startTime: data.start_time,
    endTime: data.end_time,
    userId: data.user_id,
    createdAt: data.created_at,
    endReason: data.end_reason,
    totalRuns: data.total_runs || 0,
    totalWickets: data.total_wickets || 0,
    totalOvers: data.total_overs || 0,
    extrasRuns: data.extras_runs || 0,
    inningStatus: data.inning_status || 'In Progress',
    runRate: data.run_rate || 0,
    battingCaptainId: data.batting_captain_id,
    bowlingCaptainId: data.bowling_captain_id,
    declared: data.declared || false,
    followOnEnforced: data.follow_on_enforced || false,
    note: data.note,
    isPowerplayApplied: data.is_powerplay_applied || false,
    isSuperOver: data.is_super_over || false,
    battingOrder: data.batting_order || 1,
    powerplayStartOver: data.powerplay_start_over,
    powerplayEndOver: data.powerplay_end_over,
    durationMinutes: data.duration_minutes
  };
};

export const fetchInnings = async (matchId: string): Promise<Innings[]> => {
  const { data, error } = await supabase
    .from('innings')
    .select('*')
    .eq('match_id', matchId)
    .order('innings_number', { ascending: true });

  if (error) throw error;

  return data.map((inning: any) => ({
    id: inning.id,
    matchId: inning.match_id,
    inningsNumber: inning.innings_number,
    battingTeamId: inning.batting_team_id,
    bowlingTeamId: inning.bowling_team_id,
    startTime: inning.start_time,
    endTime: inning.end_time,
    userId: inning.user_id,
    createdAt: inning.created_at,
    endReason: inning.end_reason,
    // New fields
    totalRuns: inning.total_runs || 0,
    totalWickets: inning.total_wickets || 0,
    totalOvers: inning.total_overs || 0,
    extrasRuns: inning.extras_runs || 0,
    inningStatus: inning.inning_status || 'In Progress',
    runRate: inning.run_rate || 0,
    battingCaptainId: inning.batting_captain_id,
    bowlingCaptainId: inning.bowling_captain_id,
    declared: inning.declared || false,
    followOnEnforced: inning.follow_on_enforced || false,
    note: inning.note,
    isPowerplayApplied: inning.is_powerplay_applied || false,
    isSuperOver: inning.is_super_over || false,
    battingOrder: inning.batting_order || 1,
    powerplayStartOver: inning.powerplay_start_over,
    powerplayEndOver: inning.powerplay_end_over,
    durationMinutes: inning.duration_minutes
  }));
};

export const updateInnings = async (inningsId: string, updates: {
  endTime?: string;
  endReason?: string;
  // New fields
  inningStatus?: 'In Progress' | 'Completed' | 'Abandoned';
  totalRuns?: number;
  totalWickets?: number;
  totalOvers?: number;
  extrasRuns?: number;
  runRate?: number;
  note?: string;
}): Promise<void> => {
  const { error } = await supabase
    .from('innings')
    .update({
      end_time: updates.endTime,
      end_reason: updates.endReason,
      // New fields
      inning_status: updates.inningStatus,
      total_runs: updates.totalRuns,
      total_wickets: updates.totalWickets,
      total_overs: updates.totalOvers,
      extras_runs: updates.extrasRuns,
      run_rate: updates.runRate,
      note: updates.note
    })
    .eq('id', inningsId);

  if (error) throw error;
};

export const updateInningsStats = async (
  matchId: string, 
  inningsNumber: number,
  stats: {
    totalRuns?: number;
    totalWickets?: number;
    totalOvers?: number;
    extrasRuns?: number;
    runRate?: number;
  }
): Promise<void> => {
  // First get the innings record
  const { data: innings, error: fetchError } = await supabase
    .from('innings')
    .select('id')
    .eq('match_id', matchId)
    .eq('innings_number', inningsNumber)
    .maybeSingle();

  if (fetchError) {
    console.error('Error fetching innings:', fetchError);
    return; // Don't throw error, just log and continue
  }

  if (!innings) {
    console.warn(`No innings record found for match ${matchId}, innings ${inningsNumber}`);
    return;
  }

  // Update the innings record
  const { error: updateError } = await supabase
    .from('innings')
    .update({
      total_runs: stats.totalRuns,
      total_wickets: stats.totalWickets,
      total_overs: stats.totalOvers,
      extras_runs: stats.extrasRuns,
      run_rate: stats.runRate
    } as Partial<Tables<'innings'>>)
    .eq('id', innings.id);

  if (updateError) {
    console.error('Error updating innings stats:', updateError);
  }
};

// Fielding Stats
export const createFieldingStats = async (stats: Omit<FieldingStats, 'id' | 'createdAt' | 'updatedAt'>): Promise<FieldingStats> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('fielding_stats')
    .insert({
      match_id: stats.matchId,
      player_id: stats.playerId,
      innings: stats.innings,
      catches: stats.catches,
      run_outs_direct: stats.runOutsDirect,
      run_outs_assist: stats.runOutsAssist,
      stumpings: stats.stumpings,
      missed_chances: stats.missedChances,
      user_id: session.session.user.id
    })
    .select()
    .single();

  if (error) throw error;

  return {
    id: data.id,
    matchId: data.match_id,
    playerId: data.player_id,
    innings: data.innings,
    catches: data.catches,
    runOutsDirect: data.run_outs_direct,
    runOutsAssist: data.run_outs_assist,
    stumpings: data.stumpings,
    missedChances: data.missed_chances,
    userId: data.user_id,
    createdAt: data.created_at,
    updatedAt: data.updated_at
  };
};

// Match Events
export const createMatchEvent = async (event: Omit<MatchEvent, 'id' | 'createdAt'>): Promise<MatchEvent> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('match_events')
    .insert({
      match_id: event.matchId,
      event_type: event.eventType,
      description: event.description,
      occurred_at: event.occurredAt,
      innings: event.innings,
      over_number: event.overNumber,
      ball_number: event.ballNumber,
      user_id: session.session.user.id
    })
    .select()
    .single();

  if (error) throw error;

  return {
    id: data.id,
    matchId: data.match_id,
    eventType: data.event_type,
    description: data.description,
    occurredAt: data.occurred_at,
    innings: data.innings,
    overNumber: data.over_number,
    ballNumber: data.ball_number,
    userId: data.user_id,
    createdAt: data.created_at
  };
};

// Add shadow_logo_color column to players table
export const addShadowLogoColorColumn = async () => {
  // This function is likely for migrations and not meant to be called in the app.
  // If you need to add this column, run a Supabase migration.
  // Commenting out to resolve the type error related to supabase.rpc
  /*
  const { error } = await supabase.rpc('add_shadow_logo_color_column');
  if (error) {
    console.error('Error adding shadow_logo_color column:', error);
    throw error;
  }
  */
};

// Feedback and Reports
export const fetchFeedback = async (): Promise<Feedback[]> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

   
  const { data, error } = await supabase
    .from('feedback')
    .select('*')
    .eq('user_id', session.session.user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return data.map((feedback: unknown) => {
    const f = feedback as { // Cast to a more complete type since Supabase's type might be limited
      id: string;
      user_id: string;
      type: string;
      category: string;
      title: string;
      description: string;
      priority: string;
      status: string;
      attachments?: string[];
      created_at: string;
      updated_at: string;
      resolved_at?: string;
      resolved_by?: string;
      resolution?: string;
    };
    return {
      id: f.id,
      userId: f.user_id,
      type: f.type as 'bug' | 'feature' | 'improvement' | 'general',
      category: f.category as 'scoring' | 'statistics' | 'teams' | 'players' | 'tournaments' | 'ui' | 'performance' | 'other',
      title: f.title,
      description: f.description,
      priority: f.priority as 'low' | 'medium' | 'high' | 'critical',
      status: f.status as 'open' | 'in_progress' | 'resolved' | 'closed',
      attachments: f.attachments,
      createdAt: f.created_at,
      updatedAt: f.updated_at,
      resolvedAt: f.resolved_at,
      resolvedBy: f.resolved_by,
      resolution: f.resolution,
    };
  });
};

export const createFeedback = async (feedback: CreateFeedbackPayload): Promise<Feedback> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  const attachments: string[] = [];
  if (feedback.attachments && feedback.attachments.length > 0) {
    for (const file of feedback.attachments) {
      const filePath = `feedback_attachments/${Date.now()}_${file.name}`;
      const path = await uploadImage('cricket-dashboard', filePath, file);
      attachments.push(getPublicUrl('cricket-dashboard', path));
    }
  }

   
  const { data, error } = await supabase
    .from('feedback')
    .insert({
      user_id: session.session.user.id,
      type: feedback.type,
      category: feedback.category,
      title: feedback.title,
      description: feedback.description,
      priority: feedback.priority,
      status: 'open',
      attachments: attachments.length > 0 ? attachments : null,
    })
    .select()
    .single();

  if (error) throw error;

  const feedbackData = data as unknown as {
    id: string;
    user_id: string;
    type: string;
    category: string;
    title: string;
    description: string;
    priority: string;
    status: string;
    attachments?: string[];
    created_at: string;
    updated_at: string;
    resolved_at?: string;
    resolved_by?: string;
    resolution?: string;
  };

  return {
    id: feedbackData.id,
    userId: feedbackData.user_id,
    type: feedbackData.type as 'bug' | 'feature' | 'improvement' | 'general',
    category: feedbackData.category as 'scoring' | 'statistics' | 'teams' | 'players' | 'tournaments' | 'ui' | 'performance' | 'other',
    title: feedbackData.title,
    description: feedbackData.description,
    priority: feedbackData.priority as 'low' | 'medium' | 'high' | 'critical',
    status: feedbackData.status as 'open' | 'in_progress' | 'resolved' | 'closed',
    attachments: feedbackData.attachments,
    createdAt: feedbackData.created_at,
    updatedAt: feedbackData.updated_at,
    resolvedAt: feedbackData.resolved_at,
    resolvedBy: feedbackData.resolved_by,
    resolution: feedbackData.resolution,
  };
};

export const updateFeedback = async (feedbackId: string, updates: Partial<Feedback>): Promise<Feedback> => {
   
  const { data, error } = await supabase
    .from('feedback')
    .update({
      status: updates.status,
      resolution: updates.resolution,
      resolved_at: updates.resolvedAt,
      resolved_by: updates.resolvedBy,
      updated_at: new Date().toISOString(),
    })
    .eq('id', feedbackId)
    .select()
    .single();

  if (error) throw error;

  const feedbackData = data as unknown as {
    id: string;
    user_id: string;
    type: string;
    category: string;
    title: string;
    description: string;
    priority: string;
    status: string;
    attachments?: string[];
    created_at: string;
    updated_at: string;
    resolved_at?: string;
    resolved_by?: string;
    resolution?: string;
  };

  return {
    id: feedbackData.id,
    userId: feedbackData.user_id,
    type: feedbackData.type as 'bug' | 'feature' | 'improvement' | 'general',
    category: feedbackData.category as 'scoring' | 'statistics' | 'teams' | 'players' | 'tournaments' | 'ui' | 'performance' | 'other',
    title: feedbackData.title,
    description: feedbackData.description,
    priority: feedbackData.priority as 'low' | 'medium' | 'high' | 'critical',
    status: feedbackData.status as 'open' | 'in_progress' | 'resolved' | 'closed',
    attachments: feedbackData.attachments,
    createdAt: feedbackData.created_at,
    updatedAt: feedbackData.updated_at,
    resolvedAt: feedbackData.resolved_at,
    resolvedBy: feedbackData.resolved_by,
    resolution: feedbackData.resolution,
  };
};

export const fetchReports = async (): Promise<Report[]> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

   
  const { data, error } = await supabase
    .from('reports')
    .select('*')
    .eq('user_id', session.session.user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return data.map((report: unknown) => {
    const r = report as { // Cast to a more complete type since Supabase's type might be limited
      id: string;
      user_id: string;
      type: string;
      title: string;
      description: string;
      data?: Record<string, unknown>;
      format: string;
      status: string;
      download_url?: string;
      created_at: string;
      completed_at?: string;
      error?: string;
    };
    return {
      id: r.id,
      userId: r.user_id,
      type: r.type as 'match' | 'player' | 'team' | 'tournament' | 'system',
      title: r.title,
      description: r.description,
      data: r.data,
      format: r.format as 'pdf' | 'csv' | 'json' | 'html',
      status: r.status as 'pending' | 'generating' | 'completed' | 'failed',
      downloadUrl: r.download_url,
      createdAt: r.created_at,
      completedAt: r.completed_at,
      error: r.error,
    };
  });
};

export const createReport = async (report: CreateReportPayload): Promise<Report> => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

   
  const { data, error } = await supabase
    .from('reports')
    .insert({
      user_id: session.session.user.id,
      type: report.type,
      title: report.title,
      description: report.description,
      data: report.data as Json,
      format: report.format,
      status: 'pending',
    })
    .select()
    .single();

  if (error) throw error;

  const reportData = data as unknown as {
    id: string;
    user_id: string;
    type: string;
    title: string;
    description: string;
    data?: Record<string, unknown>;
    format: string;
    status: string;
    download_url?: string;
    created_at: string;
    completed_at?: string;
    error?: string;
  };

  return {
    id: reportData.id,
    userId: reportData.user_id,
    type: reportData.type as 'match' | 'player' | 'team' | 'tournament' | 'system',
    title: reportData.title,
    description: reportData.description,
    data: reportData.data,
    format: reportData.format as 'pdf' | 'csv' | 'json' | 'html',
    status: reportData.status as 'pending' | 'generating' | 'completed' | 'failed',
    downloadUrl: reportData.download_url,
    createdAt: reportData.created_at,
    completedAt: reportData.completed_at,
    error: reportData.error,
  };
};

export const updateReportStatus = async (reportId: string, status: Report['status'], downloadUrl?: string, error?: string): Promise<void> => {
  const updates: Record<string, unknown> = {
    status,
    updated_at: new Date().toISOString(),
  };

  if (status === 'completed' && downloadUrl) {
    updates.completed_at = new Date().toISOString();
    updates.download_url = downloadUrl;
  }

  if (error) {
    updates.error = error;
  }

   
  const { error: updateError } = await supabase
    .from('reports')
    .update(updates)
    .eq('id', reportId);

  if (updateError) throw updateError;
};

// Umpires
export const fetchUmpires = async (): Promise<Umpire[]> => {
  const { data: umpires, error } = await supabase
    .from('umpires')
    .select('*');

  if (error) throw error;

  return (umpires || []).map((u: any) => ({
    umpireId: u.umpire_id,
    firstName: u.first_name,
    middleName: u.middle_name,
    lastName: u.last_name,
    fullName: u.full_name,
    dateOfBirth: u.date_of_birth || undefined,
    gender: u.gender || undefined,
    nationality: u.nationality || undefined,
    debutYear: u.debut_year || undefined,
    panelType: u.panel_type || undefined,
    primaryRole: u.primary_role || undefined,
    status: u.status || undefined,
    matchesTest: u.matches_test || undefined,
    matchesOdi: u.matches_odi || undefined,
    matchesT20i: u.matches_t20i || undefined,
    matchesFirstClass: u.matches_first_class || undefined,
    matchesListA: u.matches_list_a || undefined,
    matchesT20Domestic: u.matches_t20_domestic || undefined,
    drsDecisionsMade: u.drs_decisions_made || undefined,
    drsDecisionsUpheld: u.drs_decisions_upheld || undefined,
    drsAccuracyPercent: u.drs_accuracy_percent || undefined,
    createdAt: u.created_at || undefined,
    updatedAt: u.updated_at || undefined,
    profilePhoto: u.profile_photo || undefined,
    languagesSpoken: u.languages_spoken || undefined,
    certifications: u.certifications || undefined,
    remarks: u.remarks || undefined,
    availabilityStatus: u.availability_status || undefined,
    userId: u.user_id,
  }));
};

export const getUmpire = async (umpireId: string): Promise<Umpire | null> => {
  const { data, error } = await supabase
    .from('umpires')
    .select('*')
    .eq('umpire_id', umpireId)
    .single();
  if (error) throw error;
  if (!data) return null;
  return {
    umpireId: data.umpire_id,
    firstName: data.first_name,
    middleName: data.middle_name,
    lastName: data.last_name,
    fullName: data.full_name,
    dateOfBirth: data.date_of_birth || undefined,
    gender: data.gender || undefined,
    nationality: data.nationality || undefined,
    debutYear: data.debut_year || undefined,
    panelType: data.panel_type || undefined,
    primaryRole: data.primary_role || undefined,
    status: data.status || undefined,
    matchesTest: data.matches_test || undefined,
    matchesOdi: data.matches_odi || undefined,
    matchesT20i: data.matches_t20i || undefined,
    matchesFirstClass: data.matches_first_class || undefined,
    matchesListA: data.matches_list_a || undefined,
    matchesT20Domestic: data.matches_t20_domestic || undefined,
    drsDecisionsMade: data.drs_decisions_made || undefined,
    drsDecisionsUpheld: data.drs_decisions_upheld || undefined,
    drsAccuracyPercent: data.drs_accuracy_percent || undefined,
    createdAt: data.created_at || undefined,
    updatedAt: data.updated_at || undefined,
    profilePhoto: data.profile_photo || undefined,
    languagesSpoken: data.languages_spoken || undefined,
    certifications: data.certifications || undefined,
    remarks: data.remarks || undefined,
    availabilityStatus: data.availability_status || undefined,
    userId: data.user_id,
  };
};

export const updateUmpire = async (umpireId: string, updates: Partial<Umpire>): Promise<Umpire> => {
  // Map camelCase updates to snake_case for DB
  const dbUpdates: any = {
    first_name: updates.firstName,
    middle_name: updates.middleName,
    last_name: updates.lastName,
    date_of_birth: updates.dateOfBirth,
    gender: updates.gender,
    nationality: updates.nationality,
    debut_year: updates.debutYear,
    panel_type: updates.panelType,
    primary_role: updates.primaryRole,
    status: updates.status,
    matches_test: updates.matchesTest,
    matches_odi: updates.matchesOdi,
    matches_t20i: updates.matchesT20i,
    matches_first_class: updates.matchesFirstClass,
    matches_list_a: updates.matchesListA,
    matches_t20_domestic: updates.matchesT20Domestic,
    drs_decisions_made: updates.drsDecisionsMade,
    drs_decisions_upheld: updates.drsDecisionsUpheld,
    drs_accuracy_percent: updates.drsAccuracyPercent,
    profile_photo: updates.profilePhoto,
    languages_spoken: updates.languagesSpoken,
    certifications: updates.certifications,
    remarks: updates.remarks,
    availability_status: updates.availabilityStatus,
  };
  const { data, error } = await supabase
    .from('umpires')
    .update(dbUpdates)
    .eq('umpire_id', umpireId)
    .select()
    .single();
  if (error) throw error;
  return {
    umpireId: data.umpire_id,
    firstName: data.first_name,
    middleName: data.middle_name,
    lastName: data.last_name,
    fullName: data.full_name,
    dateOfBirth: data.date_of_birth || undefined,
    gender: data.gender || undefined,
    nationality: data.nationality || undefined,
    debutYear: data.debut_year || undefined,
    panelType: data.panel_type || undefined,
    primaryRole: data.primary_role || undefined,
    status: data.status || undefined,
    matchesTest: data.matches_test || undefined,
    matchesOdi: data.matches_odi || undefined,
    matchesT20i: data.matches_t20i || undefined,
    matchesFirstClass: data.matches_first_class || undefined,
    matchesListA: data.matches_list_a || undefined,
    matchesT20Domestic: data.matches_t20_domestic || undefined,
    drsDecisionsMade: data.drs_decisions_made || undefined,
    drsDecisionsUpheld: data.drs_decisions_upheld || undefined,
    drsAccuracyPercent: data.drs_accuracy_percent || undefined,
    createdAt: data.created_at || undefined,
    updatedAt: data.updated_at || undefined,
    profilePhoto: data.profile_photo || undefined,
    languagesSpoken: data.languages_spoken || undefined,
    certifications: data.certifications || undefined,
    remarks: data.remarks || undefined,
    availabilityStatus: data.availability_status || undefined,
    userId: data.user_id,
  };
};

export const deleteUmpire = async (umpireId: string): Promise<void> => {
  const { error } = await supabase
    .from('umpires')
    .delete()
    .eq('umpire_id', umpireId);
  if (error) throw error;
};

export { uploadProfilePicture, uploadCoverImage };

/**
 * Fetches the current live roles for a match: striker, non-striker, bowler, wicketkeeper, and fielders.
 * Aggregates from match_state, players, player_teams, and wickets tables.
 * @param matchId The match ID
 * @param currentBowlingTeamId The current bowling team ID
 * @returns {Promise<{ striker: any, nonStriker: any, bowler: any, wicketkeeper: any, fielders: any[] }>}
 */
export const fetchLiveMatchRoles = async (
  matchId: string,
  currentBowlingTeamId: string
): Promise<{
  striker: { id: string; name: string } | null;
  nonStriker: { id: string; name: string } | null;
  bowler: { id: string; name: string } | null;
  wicketkeeper: { id: string; name: string } | null;
  fielders: Array<{ id: string; name: string; fieldingPosition?: string | null }>;
}> => {
  // 1. Get match_state for striker, non-striker, bowler
  const { data: matchState, error: msError } = await supabase
    .from('match_state')
    .select('*')
    .eq('match_id', matchId)
    .single();
  if (msError) throw msError;

  // 2. Get player info for striker, non-striker, bowler
  const playerIds = [matchState.current_striker_id, matchState.current_non_striker_id, matchState.current_bowler_id].filter(Boolean);
  let playersMap: Record<string, { id: string; name: string }> = {};
  if (playerIds.length > 0) {
    const { data: players, error: pError } = await supabase
      .from('players')
      .select('id, name')
      .in('id', playerIds);
    if (pError) throw pError;
    playersMap = Object.fromEntries(players.map((p: any) => [p.id, { id: p.id, name: p.name }]));
  }
  const striker = matchState.current_striker_id ? playersMap[matchState.current_striker_id] || null : null;
  const nonStriker = matchState.current_non_striker_id ? playersMap[matchState.current_non_striker_id] || null : null;
  const bowler = matchState.current_bowler_id ? playersMap[matchState.current_bowler_id] || null : null;

  // 3. Get all players in current bowling team
  const { data: playerTeams, error: ptError } = await supabase
    .from('player_teams')
    .select('player_id')
    .eq('team_id', currentBowlingTeamId);
  if (ptError) throw ptError;
  const bowlingTeamPlayerIds = playerTeams.map((pt: any) => pt.player_id);

  // 4. Get player info for all bowling team players
  const { data: bowlingPlayers, error: bpError } = await supabase
    .from('players')
    .select('id, name, role, keeping_style, fielding_position')
    .in('id', bowlingTeamPlayerIds);
  if (bpError) throw bpError;

  // 5. Find wicketkeeper: player with keeping_style in bowling team
  let wicketkeeper = bowlingPlayers.find((p: any) => p.keeping_style);
  if (!wicketkeeper) {
    // Fallback: get most recent wicket with wicketkeeper_id
    const { data: wickets, error: wError } = await supabase
      .from('wickets')
      .select('fielder_id')
      .eq('match_id', matchId)
      .order('created_at', { ascending: false })
      .limit(1);
    if (wError) throw wError;
    const wkId = wickets && wickets.length > 0 ? wickets[0].fielder_id : null;
    if (wkId) {
      wicketkeeper = bowlingPlayers.find((p: any) => p.id === wkId);
    }
  }
  const wicketkeeperObj = wicketkeeper ? { id: wicketkeeper.id, name: wicketkeeper.name } : null;

  // 6. Fielders: all bowling team players except bowler and wicketkeeper
  const excludeIds = [matchState.current_bowler_id, wicketkeeper?.id].filter(Boolean);
  const fielders = bowlingPlayers
    .filter((p: any) => !excludeIds.includes(p.id))
    .map((p: any) => ({ id: p.id, name: p.name, fieldingPosition: p.fielding_position }));

  return {
    striker,
    nonStriker,
    bowler,
    wicketkeeper: wicketkeeperObj,
    fielders,
  };
};

// Debug function to check database state
export const debugTournaments = async () => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  console.log('Current user ID:', session.session.user.id);

  // Check all tournaments
  const { data: allTournaments, error: tournamentsError } = await supabase
    .from('tournaments')
    .select('*');

  console.log('All tournaments:', allTournaments);
  console.log('Tournaments error:', tournamentsError);

  // Check profile_tournaments for current user
  const { data: userProfileTournaments, error: profileError } = await supabase
    .from('profile_tournaments')
    .select('*')
    .eq('profile_id', session.session.user.id);

  console.log('User profile tournaments:', userProfileTournaments);
  console.log('Profile tournaments error:', profileError);

  return { allTournaments, userProfileTournaments };
};

// Debug function to check teams database state
export const debugTeams = async () => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  console.log('Current user ID:', session.session.user.id);

  // Check all teams
  const { data: allTeams, error: teamsError } = await supabase
    .from('teams')
    .select('*');

  console.log('All teams:', allTeams);
  console.log('Teams error:', teamsError);

  // Check profile_teams for current user
  const { data: userProfileTeams, error: profileError } = await supabase
    .from('profile_teams')
    .select('*')
    .eq('profile_id', session.session.user.id);

  console.log('User profile teams:', userProfileTeams);
  console.log('Profile teams error:', profileError);

  return { allTeams, userProfileTeams };
};

// Debug function to check players database state
export const debugPlayers = async () => {
  const { data: session, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  
  if (!session?.session?.user?.id) {
    throw new Error('User not authenticated');
  }

  console.log('Current user ID:', session.session.user.id);

  // Check all players
  const { data: allPlayers, error: playersError } = await supabase
    .from('players')
    .select('*');

  console.log('All players:', allPlayers);
  console.log('Players error:', playersError);

  // Check player_teams relationships
  const { data: playerTeams, error: playerTeamsError } = await supabase
    .from('player_teams')
    .select('*');

  console.log('Player teams relationships:', playerTeams);
  console.log('Player teams error:', playerTeamsError);

  // Check teams managed by current user
  const { data: userTeams, error: userTeamsError } = await supabase
    .from('profile_teams')
    .select(`
      teams (
        id,
        name,
        player_teams (
          player_id
        )
      )
    `)
    .eq('profile_id', session.session.user.id);

  console.log('User managed teams:', userTeams);
  console.log('User teams error:', userTeamsError);

  return { allPlayers, playerTeams, userTeams };
};

export const debugMatches = async () => {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) {
    console.error('Session error:', sessionError);
    return;
  }
  
  if (!sessionData?.session?.user?.id) {
    console.log('No authenticated user');
    return;
  }

  const userId = sessionData.session.user.id;
  console.log('Current user ID:', userId);

  // Check tournament access
  const { data: tournamentAccess, error: taError } = await supabase
    .from('profile_tournaments')
    .select('tournament_id, role')
    .eq('profile_id', userId);

  if (taError) {
    console.error('Error fetching tournament access:', taError);
  } else {
    console.log('Tournament access:', tournamentAccess);
  }

  // Check team access
  const { data: teamAccess, error: teaError } = await supabase
    .from('profile_teams')
    .select('team_id, role')
    .eq('profile_id', userId);

  if (teaError) {
    console.error('Error fetching team access:', teaError);
  } else {
    console.log('Team access:', teamAccess);
  }

  // Check all matches
  const { data: allMatches, error: amError } = await supabase
    .from('matches')
    .select('id, tournament_id, team1_id, team2_id, city, status');

  if (amError) {
    console.error('Error fetching all matches:', amError);
  } else {
    console.log('All matches in database:', allMatches);
  }

  // Check accessible matches
  const { data: accessibleMatches, error: acError } = await supabase
    .from('matches')
    .select('id, tournament_id, team1_id, team2_id, city, status');

  if (acError) {
    console.error('Error fetching accessible matches:', acError);
  } else {
    console.log('Accessible matches:', accessibleMatches);
  }
};

export const insertMatchUmpires = async (
  matchId: string,
  umpireAssignments: { umpire_id: string, role: string, user_id: string }[]
) => {
  if (umpireAssignments.length === 0) return;
  const assignments = umpireAssignments.map(a => ({
    match_id: matchId,
    umpire_id: a.umpire_id,
    role: a.role,
    user_id: a.user_id,
  }));
  const { error } = await supabase
    .from('match_umpires')
    .insert(assignments);
  if (error) throw error;
};

export const fetchMatchUmpires = async (matchId: string) => {
  const { data, error } = await supabase
    .from('match_umpires')
    .select(`
      role,
      umpire_id,
      umpires (
        umpire_id,
        first_name,
        last_name,
        full_name
      )
    `)
    .eq('match_id', matchId);
  if (error) throw error;
  return data;
};
